import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, X, CircleDashed, Circle, BellRing, Wrench, Search, Check, BellOff, CircleOff } from "lucide-react";
import { StatusChangeForm } from "./StatusChangeForm";

const ICON_MAP = {
  'circle-dashed': CircleDashed,
  'circle': Circle,
  'bell-ring': BellRing,
  'wrench': Wrench,
  'search': Search,
  'check': Check,
  'bell-off': BellOff,
  'circle-off': CircleOff,
} as const;

const getLucideIcon = (iconId: string | null | undefined) => {
  if (!iconId) return ArrowRight;
  return ICON_MAP[iconId as keyof typeof ICON_MAP] || ArrowRight;
};

interface StatusDetails {
  name: string;
  description_customer_facing?: string;
  action_verb?: string;
  action_description?: string;
  lucide_icon_id?: string;
  requires_reason?: boolean;
  available_reasons?: { name: string }[];
}

interface StatusCardProps {
  statusName: string;
  statusDetails?: StatusDetails;
  isExpanded: boolean;
  isExpanding: boolean;
  isProcessing: boolean;
  hasOtherSelection: boolean;
  animationDuration: number;
  onSelect: () => void;
  onCancel: () => void;
  onSubmit: (comments: string, reason?: string) => Promise<void>;
}

export const StatusCard: React.FC<StatusCardProps> = ({
  statusName,
  statusDetails,
  isExpanded,
  isExpanding,
  isProcessing,
  hasOtherSelection,
  animationDuration,
  onSelect,
  onCancel,
  onSubmit
}) => {
  const IconComponent = getLucideIcon(statusDetails?.lucide_icon_id);

  const getCardClasses = () => {
    if (isExpanded) {
      return 'w-full md:flex-1 border-blue-300 bg-white shadow-md';
    }
    if (hasOtherSelection) {
      return 'w-full h-0 md:w-0 md:h-auto opacity-0 border-gray-200 bg-white pointer-events-none';
    }
    return 'w-full md:w-auto md:flex-shrink-0 border-gray-200 bg-white hover:border-blue-200 hover:bg-blue-25 shadow-sm hover:shadow-md';
  };

  const canClick = !isExpanded && !isProcessing && !hasOtherSelection;

  return (
    <div
      className={`
        relative overflow-hidden rounded-lg border transition-all ease-in-out cursor-pointer
        ${getCardClasses()}
      `}
      style={{
        transitionDuration: `${animationDuration}ms`
      }}
      onClick={canClick ? onSelect : undefined}
    >
      <div className="h-full flex flex-col">
        <div className="flex items-center p-4">
          <div className={`
            flex items-center justify-center rounded-full transition-all duration-300 flex-shrink-0
            ${isExpanded
              ? 'w-12 h-12 bg-gray-100 text-thalo-blue'
              : 'w-10 h-10 bg-gray-100 text-gray-600 group-hover:bg-blue-100 group-hover:text-blue-600'
            }
          `}>
            <IconComponent className={`${isExpanded ? 'h-6 w-6' : 'h-5 w-5'} transition-all duration-300`} />
          </div>

          <div className={`${isExpanded ? 'ml-4' : 'ml-3'} transition-all duration-300 ${isExpanded ? 'flex-1' : 'flex-1 md:flex-shrink'}`}>
            <h3 className={`
              font-medium transition-all duration-300
              ${isExpanded
                ? 'text-blue-900 text-lg mb-1 whitespace-normal'
                : 'text-gray-900 text-sm whitespace-nowrap'
              }
            `}>
              {statusDetails?.action_verb || statusName}
            </h3>
            {!isExpanding && isExpanded && (statusDetails?.action_description || statusDetails?.description_customer_facing) && (
              <div className="animate-in slide-in-from-top-2 fade-in duration-300">
                <p className="text-sm">
                  {statusDetails?.action_description || statusDetails?.description_customer_facing}
                </p>
              </div>
            )}
          </div>

          {isExpanded && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onCancel();
              }}
              className="h-8 w-8 p-0 text-thalo-blue hover:text-thalo-blueLight hover:bg-blue-100 flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}

          {!isExpanded && !hasOtherSelection && (
            <ArrowRight className="h-4 w-4 text-gray-400 ml-2 flex-shrink-0" />
          )}
        </div>

        {!isExpanding && isExpanded && (
          <StatusChangeForm
            statusName={statusName}
            isProcessing={isProcessing}
            requiresReason={statusDetails?.requires_reason}
            availableReasons={statusDetails?.available_reasons}
            onSubmit={onSubmit}
            onCancel={onCancel}
          />
        )}
      </div>
    </div>
  );
};